// Mesure d'un texte ou d'une formule SANS navigateur : la validation (Studio
// ET serveur) contrôle la mise en page d'une animation — textes qui se
// chevauchent, cadre trop petit, texte coupé par un trait. Une IA ne connaît
// pas la largeur d'un texte ; ce module la calcule avec les vraies chasses
// des polices de la scène (Inter, KaTeX : `font-metrics.ts`).
//
// Une ESTIMATION (pas de crénage, mise en page TeX simplifiée), calée sur le
// rendu de Chromium à quelques pour cent près (`measure.test.ts`). Le cadre
// `box` d'un texte, lui, est dessiné par le navigateur autour du texte
// réellement rendu : jamais estimé.

import { INTER_REGULAR, INTER_SEMIBOLD, KATEX_MAIN, KATEX_MATH } from './font-metrics';
import { splitDisplayText } from './tex';

/** KaTeX agrandit ses formules : `.katex { font-size: 1.21em }`. */
export const KATEX_SCALE = 1.21;
/** Interligne des textes de la scène (`leading-tight`). */
export const TEXT_LINE_HEIGHT = 1.25;
/** Marges intérieures du bloc d'une formule (`px-2 py-1`), en px du canevas. */
export const FORMULA_PAD_X = 8;
export const FORMULA_PAD_Y = 4;

export interface Extent {
  /** boîte de mise en page (celle que centre l'ancrage `at`) */
  width: number;
  height: number;
  /** encre : partie réellement dessinée, centrée verticalement dans la boîte */
  inkWidth: number;
  inkHeight: number;
  /** décalage vertical du centre de l'encre par rapport au centre de la boîte */
  inkOffsetY: number;
  lines: number;
}

// ------------------------------------------------------------------- texte

const FALLBACK_TEXT_EM = 0.6;

function baseLetter(ch: string): string {
  return ch.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/** Chasse d'un caractère d'Inter, en em. */
export function interCharEm(ch: string, bold = false): number {
  const table = bold ? INTER_SEMIBOLD : INTER_REGULAR;
  const w = table[ch] ?? table[baseLetter(ch)];
  return w !== undefined ? w / 1000 : FALLBACK_TEXT_EM;
}

/** Largeur (px) d'un texte SANS formule sur une ligne. */
export function plainTextWidth(text: string, size: number, bold = false): number {
  let em = 0;
  for (const ch of text) em += interCharEm(ch, bold);
  return em * size;
}

interface Word {
  width: number;
  /** hauteur et profondeur max (px) au-dessus / au-dessous de la ligne de base */
  above: number;
  below: number;
  space: number;
}

/** Mots d'un texte avec formules `$…$` (une formule ne se coupe pas). */
function words(text: string, size: number, bold: boolean): Word[] {
  const space = plainTextWidth(' ', size, bold);
  const out: Word[] = [];
  let cur: Word | null = null;
  const flush = () => {
    if (cur) out.push(cur);
    cur = null;
  };
  const textAbove = 0.75 * size;
  const textBelow = 0.22 * size;
  for (const part of splitDisplayText(text)) {
    if (part.kind === 'math') {
      const m = texBox(part.value);
      const k = KATEX_SCALE * size;
      cur ??= { width: 0, above: textAbove, below: textBelow, space };
      cur.width += m.w * k;
      cur.above = Math.max(cur.above, m.h * k);
      cur.below = Math.max(cur.below, m.d * k);
      continue;
    }
    const chunks = part.value.split(/([ \t\n]+)/);
    for (const chunk of chunks) {
      if (!chunk) continue;
      if (/^[ \t\n]+$/.test(chunk)) {
        flush();
        continue;
      }
      cur ??= { width: 0, above: textAbove, below: textBelow, space };
      cur.width += plainTextWidth(chunk, size, bold);
    }
  }
  flush();
  return out;
}

/**
 * Boîte d'un objet `text` (repère du canevas) : une ligne, ou plusieurs
 * coupées aux espaces si `maxWidth` est donné (comme le navigateur).
 */
export function measureText(
  text: string,
  size: number,
  weight: 'normal' | 'bold' = 'normal',
  maxWidth?: number,
): Extent {
  const bold = weight === 'bold';
  const ws = words(text, size, bold);
  const lineH = TEXT_LINE_HEIGHT * size;
  if (ws.length === 0) return empty(lineH);
  const lines: Word[][] = [[]];
  let lineW = 0;
  for (const w of ws) {
    const line = lines[lines.length - 1]!;
    const add = line.length ? w.space + w.width : w.width;
    if (maxWidth !== undefined && line.length && lineW + add > maxWidth + 0.5) {
      lines.push([w]);
      lineW = w.width;
    } else {
      line.push(w);
      lineW += add;
    }
  }
  let widest = 0;
  let height = 0;
  let inkHeight = 0;
  for (const line of lines) {
    const w = line.reduce((s, x, i) => s + x.width + (i ? x.space : 0), 0);
    widest = Math.max(widest, w);
    // une formule haute (fraction) agrandit sa ligne
    const above = Math.max(...line.map((x) => x.above));
    const below = Math.max(...line.map((x) => x.below));
    const extra = Math.max(0, above - 0.75 * size) + Math.max(0, below - 0.22 * size);
    height += lineH + extra;
    inkHeight += lineH + extra;
  }
  // l'encre d'un texte : de la hauteur des capitales au bas des jambages
  inkHeight -= 0.25 * size;
  return {
    width: maxWidth !== undefined ? maxWidth : widest,
    height,
    inkWidth: widest,
    inkHeight,
    inkOffsetY: 0.05 * size,
    lines: lines.length,
  };
}

function empty(lineH: number): Extent {
  return { width: 0, height: lineH, inkWidth: 0, inkHeight: 0, inkOffsetY: 0, lines: 1 };
}

/** Boîte d'un objet `formula` (KaTeX en ligne dans un bloc `px-2 py-1`). */
export function measureFormula(tex: string, size: number): Extent {
  const m = texBox(tex);
  const k = KATEX_SCALE * size;
  // ligne de base : la boîte de ligne englobe la police du bloc (Inter,
  // interligne 1,5) et celle de KaTeX (interligne 1,2) ; une fraction la dépasse
  const top = Math.max(1.114 * size, 1.0285 * size, m.h * k);
  const bottom = Math.max(0.387 * size, 0.4235 * size, m.d * k);
  const inkTop = Math.max(m.h, 0.5) * k;
  const inkBottom = m.d * k;
  const height = top + bottom + 2 * FORMULA_PAD_Y;
  // centre de l'encre par rapport au centre de la boîte
  const boxCenterFromBaseline = (bottom - top) / 2;
  const inkCenterFromBaseline = (inkBottom - inkTop) / 2;
  return {
    width: m.w * k + 2 * FORMULA_PAD_X,
    height,
    inkWidth: m.w * k,
    inkHeight: inkTop + inkBottom,
    inkOffsetY: inkCenterFromBaseline - boxCenterFromBaseline,
    lines: 1,
  };
}

/** Boîte d'un objet `value` (chiffres semi-gras, préfixe et suffixe). */
export function measureValue(text: string, size: number): Extent {
  const e = measureText(text, size, 'bold');
  return e;
}

// ------------------------------------------------------------------- TeX

/** Dimensions TeX en em de la taille de la formule : chasse, hauteur, profondeur. */
export interface TexDims {
  w: number;
  h: number;
  d: number;
}

type AtomClass = 'ord' | 'op' | 'bin' | 'rel' | 'open' | 'close' | 'punct' | 'inner';
/** 0 : texte (ou affichage), 1 : indice, 2 : indice d'indice */
type Style = 0 | 1 | 2;

interface Atom extends TexDims {
  cls: AtomClass;
}

const STYLE_SCALE = [1, 0.7, 0.5] as const;
const AXIS = 0.25;
const RULE = 0.04;
const NULL_DELIM = 0.12;
const SCRIPT_SPACE = 0.05;

type Tok =
  | { t: 'cmd'; v: string }
  | { t: 'char'; v: string }
  | { t: '{' }
  | { t: '}' }
  | { t: '^' }
  | { t: '_' }
  | { t: '&' }
  | { t: '[' }
  | { t: ']' };

function tokenize(tex: string): Tok[] {
  const out: Tok[] = [];
  const chars = [...tex];
  for (let i = 0; i < chars.length; i++) {
    const c = chars[i]!;
    if (c === '\\') {
      let name = '';
      let j = i + 1;
      while (j < chars.length && /[a-zA-Z]/.test(chars[j]!)) name += chars[j++];
      if (!name && j < chars.length) name = chars[j++]!;
      out.push({ t: 'cmd', v: name });
      i = j - 1;
      // espaces après une commande nommée : ignorés (comme TeX)
      continue;
    }
    if (c === '{' || c === '}' || c === '^' || c === '_' || c === '&' || c === '[' || c === ']')
      out.push({ t: c } as Tok);
    else if (c === '%') break;
    else out.push({ t: 'char', v: c });
  }
  return out;
}

const REL_CHARS = new Set([...'=<>:→←↔⇒⇐⇔≈≤≥≠≡∈∉⊂⊃⊆⊇∼≃≅∝↦⟶⟹⟺∣∥⊥']);
const BIN_CHARS = new Set([...'+−×÷·∗∘±∓∪∩⋅∙']);
const OPEN_CHARS = new Set([...'([⟨']);
const CLOSE_CHARS = new Set([...')]⟩!?']);
const PUNCT_CHARS = new Set([...',;']);

/** symboles nommés : [caractère dont on prend la chasse, classe] */
const SYMBOLS: Record<string, readonly [string, AtomClass]> = {
  times: ['×', 'bin'],
  cdot: ['⋅', 'bin'],
  div: ['÷', 'bin'],
  pm: ['±', 'bin'],
  mp: ['∓', 'bin'],
  ast: ['∗', 'bin'],
  circ: ['∘', 'bin'],
  bullet: ['∙', 'bin'],
  cup: ['∪', 'bin'],
  cap: ['∩', 'bin'],
  setminus: ['\\', 'bin'],
  to: ['→', 'rel'],
  rightarrow: ['→', 'rel'],
  leftarrow: ['←', 'rel'],
  gets: ['←', 'rel'],
  leftrightarrow: ['↔', 'rel'],
  Rightarrow: ['⇒', 'rel'],
  Leftarrow: ['⇐', 'rel'],
  Leftrightarrow: ['⇔', 'rel'],
  longrightarrow: ['⟶', 'rel'],
  Longrightarrow: ['⟹', 'rel'],
  Longleftrightarrow: ['⟺', 'rel'],
  mapsto: ['↦', 'rel'],
  approx: ['≈', 'rel'],
  simeq: ['≃', 'rel'],
  sim: ['∼', 'rel'],
  cong: ['≅', 'rel'],
  equiv: ['≡', 'rel'],
  neq: ['=', 'rel'],
  ne: ['=', 'rel'],
  leq: ['≤', 'rel'],
  le: ['≤', 'rel'],
  geq: ['≥', 'rel'],
  ge: ['≥', 'rel'],
  leqslant: ['≤', 'rel'],
  geqslant: ['≥', 'rel'],
  propto: ['∝', 'rel'],
  in: ['∈', 'rel'],
  notin: ['∉', 'rel'],
  subset: ['⊂', 'rel'],
  supset: ['⊃', 'rel'],
  subseteq: ['⊆', 'rel'],
  supseteq: ['⊇', 'rel'],
  perp: ['⊥', 'rel'],
  parallel: ['∥', 'rel'],
  mid: ['∣', 'rel'],
  infty: ['∞', 'ord'],
  partial: ['∂', 'ord'],
  nabla: ['∇', 'ord'],
  forall: ['∀', 'ord'],
  exists: ['∃', 'ord'],
  emptyset: ['∅', 'ord'],
  varnothing: ['∅', 'ord'],
  angle: ['∠', 'ord'],
  prime: ['′', 'ord'],
  ldots: ['…', 'inner'],
  dots: ['…', 'inner'],
  cdots: ['⋯', 'inner'],
  dotsc: ['…', 'inner'],
  lbrace: ['{', 'open'],
  rbrace: ['}', 'close'],
  '{': ['{', 'open'],
  '}': ['}', 'close'],
  langle: ['⟨', 'open'],
  rangle: ['⟩', 'close'],
  vert: ['|', 'ord'],
  '|': ['‖', 'ord'],
  Vert: ['‖', 'ord'],
  '%': ['%', 'ord'],
  $: ['$', 'ord'],
  '&': ['&', 'ord'],
  '#': ['#', 'ord'],
  _: ['_', 'ord'],
  degree: ['°', 'ord'],
};

const GREEK: Record<string, string> = {
  alpha: 'α',
  beta: 'β',
  gamma: 'γ',
  delta: 'δ',
  epsilon: 'ϵ',
  varepsilon: 'ε',
  zeta: 'ζ',
  eta: 'η',
  theta: 'θ',
  vartheta: 'ϑ',
  iota: 'ι',
  kappa: 'κ',
  lambda: 'λ',
  mu: 'μ',
  nu: 'ν',
  xi: 'ξ',
  pi: 'π',
  varpi: 'ϖ',
  rho: 'ρ',
  varrho: 'ϱ',
  sigma: 'σ',
  varsigma: 'ς',
  tau: 'τ',
  upsilon: 'υ',
  phi: 'ϕ',
  varphi: 'φ',
  chi: 'χ',
  psi: 'ψ',
  omega: 'ω',
  Gamma: 'Γ',
  Delta: 'Δ',
  Theta: 'Θ',
  Lambda: 'Λ',
  Xi: 'Ξ',
  Pi: 'Π',
  Sigma: 'Σ',
  Upsilon: 'Υ',
  Phi: 'Φ',
  Psi: 'Ψ',
  Omega: 'Ω',
};

const SPACES: Record<string, number> = {
  ',': 3 / 18,
  ':': 4 / 18,
  '>': 4 / 18,
  ';': 5 / 18,
  '!': -3 / 18,
  ' ': 0.25,
  quad: 1,
  qquad: 2,
  enspace: 0.5,
  thinspace: 3 / 18,
  medspace: 4 / 18,
  thickspace: 5 / 18,
  negthinspace: -3 / 18,
};

const FUNCTIONS = new Set(
  'sin cos tan cot sec csc arcsin arccos arctan sinh cosh tanh ln log lg exp lim liminf limsup max min sup inf det deg dim ker arg gcd Pr hom'.split(
    ' ',
  ),
);

/** commandes de police : leur argument est mesuré lettre à lettre en romain */
const ROMAN_ARG = new Set(['mathrm', 'operatorname', 'mathsf', 'mathtt', 'mathbb', 'mathcal']);
const TEXT_ARG = new Set(['text', 'textrm', 'textnormal', 'mbox', 'textsf', 'texttt', 'textup']);
const BOLD_ARG = new Set(['mathbf', 'textbf', 'boldsymbol', 'bm', 'pmb']);
const ITALIC_TEXT_ARG = new Set(['textit', 'emph']);
const ACCENTS: Record<string, number> = {
  vec: 0.26,
  hat: 0.2,
  widehat: 0.26,
  bar: 0.16,
  overline: 0.16,
  tilde: 0.2,
  widetilde: 0.26,
  dot: 0.16,
  ddot: 0.16,
  check: 0.2,
  breve: 0.2,
  acute: 0.2,
  grave: 0.2,
  mathring: 0.2,
  overrightarrow: 0.36,
  overleftarrow: 0.36,
  overleftrightarrow: 0.36,
  underline: 0,
};
/** commandes sans effet de taille, dont on mesure seulement l'argument */
const TRANSPARENT_ARG = new Set([
  'mathit',
  'mathnormal',
  'cancel',
  'bcancel',
  'xcancel',
  'displaylines',
  'phantom',
  'underline',
  'mathbin',
  'mathrel',
  'mathord',
  'mathop',
]);
/** commandes à ignorer (styles, couleurs posées pour la suite) */
const IGNORED = new Set(['limits', 'nolimits', 'displaystyle', 'textstyle', 'left.', 'right.']);

const DELIM_NORMAL: Record<string, number> = {
  '(': 0.389,
  ')': 0.389,
  '[': 0.278,
  ']': 0.278,
  '{': 0.5,
  '}': 0.5,
  '|': 0.278,
  '‖': 0.5,
  '⟨': 0.389,
  '⟩': 0.389,
  '.': 0,
};
/** chasses des délimiteurs agrandis (Size1…Size4) et hauteur totale couverte */
const DELIM_SIZES: { total: number; w: Record<string, number> }[] = [
  { total: 1.2, w: { '(': 0.458, '[': 0.417, '{': 0.583, '|': 0.333, '⟨': 0.472 } },
  { total: 1.8, w: { '(': 0.597, '[': 0.472, '{': 0.667, '|': 0.333, '⟨': 0.472 } },
  { total: 2.4, w: { '(': 0.736, '[': 0.528, '{': 0.75, '|': 0.333, '⟨': 0.528 } },
  { total: 3.0, w: { '(': 0.792, '[': 0.583, '{': 0.806, '|': 0.333, '⟨': 0.611 } },
];
const DELIM_MIRROR: Record<string, string> = { ')': '(', ']': '[', '}': '{', '⟩': '⟨', '‖': '|' };

function glyph(ch: string, italic: boolean, scale: number): TexDims {
  const fromMath = italic ? KATEX_MATH[ch] : undefined;
  const g = fromMath ?? KATEX_MAIN[ch] ?? KATEX_MATH[ch] ?? KATEX_MAIN[baseLetter(ch)];
  if (!g) return { w: 0.6 * scale, h: 0.7 * scale, d: 0 };
  return { w: (g[0] / 1000) * scale, h: (g[1] / 1000) * scale, d: (g[2] / 1000) * scale };
}

function charClass(ch: string): AtomClass {
  if (REL_CHARS.has(ch)) return 'rel';
  if (BIN_CHARS.has(ch) || ch === '-' || ch === '*') return 'bin';
  if (OPEN_CHARS.has(ch)) return 'open';
  if (CLOSE_CHARS.has(ch)) return 'close';
  if (PUNCT_CHARS.has(ch)) return 'punct';
  return 'ord';
}

/** espace entre deux atomes (TeX), en mu ; `true` : seulement en style texte */
const SPACING: Record<AtomClass, Partial<Record<AtomClass, readonly [number, boolean]>>> = {
  ord: { op: [3, false], bin: [4, true], rel: [5, true], inner: [3, true] },
  op: { ord: [3, false], op: [3, false], rel: [5, true], inner: [3, true] },
  bin: { ord: [4, true], op: [4, true], open: [4, true], inner: [4, true] },
  rel: { ord: [5, true], op: [5, true], open: [5, true], inner: [5, true] },
  open: {},
  close: { op: [3, false], bin: [4, true], rel: [5, true], inner: [3, true] },
  punct: {
    ord: [3, true],
    op: [3, true],
    rel: [3, true],
    open: [3, true],
    close: [3, true],
    punct: [3, true],
    inner: [3, true],
  },
  inner: {
    ord: [3, true],
    op: [3, false],
    bin: [4, true],
    rel: [5, true],
    open: [3, true],
    punct: [3, true],
    inner: [3, true],
  },
};

class TexParser {
  private i = 0;
  /** > 0 dans l'argument d'un `\mathrm{…}` mesuré comme une liste : lettres droites */
  private upright = 0;
  constructor(private readonly toks: Tok[]) {}

  private peek(): Tok | undefined {
    return this.toks[this.i];
  }

  /**
   * Le groupe `{…}` qui suit contient-il des maths (commande, indice,
   * exposant, opérateur) ? `\mathrm{H_2O + H^+ \longrightarrow H_3O^+}` :
   * oui ; `\mathrm{mol}` : non.
   */
  private groupHasMath(): boolean {
    if (this.peek()?.t !== '{') return false;
    let depth = 0;
    for (let j = this.i; j < this.toks.length; j++) {
      const x = this.toks[j]!;
      if (x.t === '{') depth++;
      else if (x.t === '}') {
        depth--;
        if (depth === 0) return false;
      } else if (x.t === 'cmd' || x.t === '^' || x.t === '_') return true;
      else if (x.t === 'char' && /[+\-=<>,;:|/*]/.test(x.v)) return true;
    }
    return false;
  }

  /** liste jusqu'à `}` (consommée) ou la fin ; `stop` : jeton qui arrête aussi */
  list(style: Style, stop?: (t: Tok) => boolean): Atom[] {
    const atoms: Atom[] = [];
    let st = style;
    while (this.i < this.toks.length) {
      const t = this.peek()!;
      if (t.t === '}') {
        this.i++;
        break;
      }
      if (stop?.(t)) break;
      if (t.t === 'cmd' && (t.v === 'scriptstyle' || t.v === 'scriptscriptstyle')) {
        this.i++;
        st = t.v === 'scriptstyle' ? 1 : 2;
        continue;
      }
      if (t.t === 'cmd' && (t.v === 'displaystyle' || t.v === 'textstyle')) {
        this.i++;
        st = 0;
        continue;
      }
      if (t.t === '^' || t.t === '_') {
        // indice sans base : base vide
        atoms.push(this.scripts({ cls: 'ord', w: 0, h: 0, d: 0 }, st));
        continue;
      }
      const atom = this.atom(st);
      if (atom) atoms.push(this.scripts(atom, st));
    }
    return atoms;
  }

  /** argument : `{…}` ou un seul jeton */
  arg(style: Style): TexDims {
    const t = this.peek();
    if (!t) return { w: 0, h: 0, d: 0 };
    if (t.t === '{') {
      this.i++;
      return hlist(this.list(style), style);
    }
    const a = this.atom(style);
    return a ?? { w: 0, h: 0, d: 0 };
  }

  /** argument brut (texte d'un `\text{…}`) */
  rawArg(): string {
    const t = this.peek();
    if (!t) return '';
    if (t.t !== '{') {
      this.i++;
      return t.t === 'char' ? t.v : t.t === 'cmd' ? '' : '';
    }
    this.i++;
    let depth = 1;
    let out = '';
    while (this.i < this.toks.length) {
      const x = this.toks[this.i++]!;
      if (x.t === '{') depth++;
      else if (x.t === '}') {
        depth--;
        if (depth === 0) break;
      }
      if (x.t === 'char') out += x.v;
      else if (x.t === 'cmd') out += x.v.length === 1 ? x.v : ' ';
    }
    return out;
  }

  /** argument optionnel `[…]` */
  optArg(style: Style): TexDims | null {
    if (this.peek()?.t !== '[') return null;
    this.i++;
    const atoms = this.list(style, (t) => t.t === ']');
    if (this.peek()?.t === ']') this.i++;
    return hlist(atoms, style);
  }

  private delimiter(): string {
    const t = this.toks[this.i++];
    if (!t) return '.';
    if (t.t === 'char') return t.v === '.' ? '.' : t.v;
    if (t.t === '[' || t.t === ']') return t.t;
    if (t.t === 'cmd') return SYMBOLS[t.v]?.[0] ?? (t.v === '|' ? '‖' : '(');
    return '.';
  }

  private scripts(base: Atom, style: Style): Atom {
    let sup: TexDims | null = null;
    let sub: TexDims | null = null;
    for (;;) {
      const t = this.peek();
      if (t?.t === '^' && !sup) {
        this.i++;
        sup = this.arg(Math.min(2, style + 1) as Style);
      } else if (t?.t === '_' && !sub) {
        this.i++;
        sub = this.arg(Math.min(2, style + 1) as Style);
      } else if (t?.t === 'char' && t.v === "'" && !sup) {
        this.i++;
        sup = glyph('′', false, STYLE_SCALE[Math.min(2, style + 1)]!);
      } else break;
    }
    if (!sup && !sub) return base;
    const s = STYLE_SCALE[style];
    let h = base.h;
    let d = base.d;
    if (sup) h = Math.max(h, Math.max(0.413 * s, base.h - 0.386 * s) + sup.h);
    if (sub) {
      const shift = Math.max(sup ? 0.247 * s : 0.15 * s, base.d + 0.05 * s);
      d = Math.max(d, shift + sub.d);
    }
    const w = base.w + Math.max(sup?.w ?? 0, sub?.w ?? 0) + SCRIPT_SPACE * s;
    return { cls: base.cls, w, h, d };
  }

  private atom(style: Style): Atom | null {
    const t = this.toks[this.i++]!;
    const s = STYLE_SCALE[style];
    switch (t.t) {
      case '{': {
        const inner = hlist(this.list(style), style);
        return { cls: 'ord', ...inner };
      }
      case '}':
        return null;
      case '&':
      case '[':
      case ']':
        return t.t === '&'
          ? null
          : { cls: t.t === '[' ? 'open' : 'close', ...glyph(t.t, false, s) };
      case '^':
      case '_':
        return null;
      case 'char': {
        if (/\s/.test(t.v)) return null;
        if (t.v === '~') return { cls: 'ord', w: 0.333 * s, h: 0, d: 0 };
        const ch = t.v === '-' ? '−' : t.v === '*' ? '∗' : t.v;
        const italic = this.upright === 0 && (/[a-zA-Z]/.test(ch) || /[α-ω]/.test(ch));
        return { cls: charClass(ch), ...glyph(ch, italic, s) };
      }
      case 'cmd':
        return this.command(t.v, style);
    }
  }

  private command(name: string, style: Style): Atom | null {
    const s = STYLE_SCALE[style];
    if (name in SPACES)
      return { cls: 'ord', w: SPACES[name]! * s, h: 0, d: 0, spacer: true } as Atom;
    if (IGNORED.has(name)) return null;
    const sym = SYMBOLS[name];
    if (sym) return { cls: sym[1], ...glyph(sym[0], false, s) };
    const greek = GREEK[name];
    if (greek) return { cls: 'ord', ...glyph(greek, /[α-ω]|[ϵϑϖϱςϕ]/.test(greek), s) };
    if (FUNCTIONS.has(name)) return { cls: 'op', ...romanWord(name, s) };
    if (TEXT_ARG.has(name) || ITALIC_TEXT_ARG.has(name))
      return { cls: 'ord', ...romanWord(this.rawArg(), s, true) };
    if (ROMAN_ARG.has(name)) {
      const cls = name === 'operatorname' ? 'op' : 'ord';
      // indices, flèches, opérateurs : mesurés comme une liste (lettres droites),
      // sinon `\mathrm{A \longrightarrow B}` perdait sa flèche et ses espacements
      if (this.groupHasMath()) {
        this.upright++;
        const a = this.arg(style);
        this.upright--;
        return { cls, ...a };
      }
      const word = romanWord(this.rawArg().replace(/\s+/g, ''), s);
      return { cls, ...word };
    }
    if (BOLD_ARG.has(name)) {
      const a = this.arg(style);
      return { cls: 'ord', w: a.w * 1.1, h: a.h, d: a.d };
    }
    if (name in ACCENTS) {
      const a = this.arg(style);
      const wide = name.startsWith('over');
      return {
        cls: 'ord',
        w: wide ? Math.max(a.w, 0.9 * s) : a.w,
        h: a.h + ACCENTS[name]! * s,
        d: name === 'underline' ? a.d + 0.2 * s : a.d,
      };
    }
    if (TRANSPARENT_ARG.has(name)) return { cls: 'ord', ...this.arg(style) };
    switch (name) {
      case 'frac':
      case 'dfrac':
      case 'tfrac':
      case 'cfrac':
      case 'binom':
      case 'dbinom':
      case 'tbinom': {
        const display = name.startsWith('d') || name === 'cfrac';
        const inner = (display ? 0 : Math.min(2, style + 1)) as Style;
        const num = this.arg(inner);
        const den = this.arg(inner);
        const f = fraction(num, den, display && style === 0, s);
        if (name.endsWith('binom')) {
          const dw = delimWidth('(', f.h, f.d, s);
          return { cls: 'inner', w: f.w + 2 * dw, h: f.h, d: f.d };
        }
        return { cls: 'inner', ...f };
      }
      case 'sqrt': {
        const index = this.optArg(2);
        const x = this.arg(style);
        const total = x.h + x.d;
        const surd = (total <= 0.9 * s ? 0.853 : total <= 1.3 * s ? 1.02 : 1.1) * s;
        const w = x.w + surd + (index ? Math.max(0, index.w - 0.3 * s) : 0);
        return { cls: 'ord', w, h: x.h + 0.16 * s, d: Math.max(x.d, 0.06 * s) };
      }
      case 'left': {
        const open = this.delimiter();
        const inner = this.list(style, (t) => t.t === 'cmd' && t.v === 'right');
        const content = hlist(inner, style);
        let close = '.';
        if (this.peek()?.t === 'cmd' && (this.peek() as { v: string }).v === 'right') {
          this.i++;
          close = this.delimiter();
        }
        const w =
          content.w +
          delimWidth(open, content.h, content.d, s) +
          delimWidth(close, content.h, content.d, s);
        const h = Math.max(content.h, 0.75 * s);
        const d = Math.max(content.d, 0.25 * s);
        return { cls: 'inner', w, h, d };
      }
      case 'right':
      case 'middle': {
        this.delimiter();
        return null;
      }
      case 'big':
      case 'Big':
      case 'bigg':
      case 'Bigg':
      case 'bigl':
      case 'bigr':
      case 'Bigl':
      case 'Bigr':
      case 'biggl':
      case 'biggr':
      case 'Biggl':
      case 'Biggr':
      case 'bigm':
      case 'Bigm': {
        const level = name.startsWith('Bigg')
          ? 3
          : name.startsWith('bigg')
            ? 2
            : name.startsWith('Big')
              ? 1
              : 0;
        const ch = this.delimiter();
        const size = DELIM_SIZES[level]!;
        const key = DELIM_MIRROR[ch] ?? ch;
        return {
          cls: name.endsWith('l') ? 'open' : name.endsWith('r') ? 'close' : 'ord',
          w: (size.w[key] ?? 0.4) * s,
          h: (size.total / 2 + AXIS) * s,
          d: (size.total / 2 - AXIS) * s,
        };
      }
      case 'color':
        this.rawArg();
        return null;
      case 'textcolor': {
        this.rawArg();
        return { cls: 'ord', ...this.arg(style) };
      }
      case 'colorbox':
      case 'fcolorbox': {
        this.rawArg();
        if (name === 'fcolorbox') this.rawArg();
        const word = romanWord(this.rawArg(), s, true);
        return { cls: 'ord', w: word.w + 0.6 * s, h: word.h + 0.3 * s, d: word.d + 0.3 * s };
      }
      case 'boxed': {
        const a = this.arg(style);
        return { cls: 'ord', w: a.w + 0.68 * s, h: a.h + 0.34 * s, d: a.d + 0.34 * s };
      }
      case 'hphantom': {
        const a = this.arg(style);
        return { cls: 'ord', w: a.w, h: 0, d: 0 };
      }
      case 'vphantom': {
        const a = this.arg(style);
        return { cls: 'ord', w: 0, h: a.h, d: a.d };
      }
      case 'rlap':
      case 'llap':
      case 'mathrlap':
      case 'mathllap':
      case 'clap':
      case 'mathclap': {
        const a = this.arg(style);
        return { cls: 'ord', w: 0, h: a.h, d: a.d };
      }
      case 'hspace':
      case 'mkern':
      case 'mskip':
      case 'kern': {
        const raw = name === 'hspace' ? this.rawArg() : this.rawDimension();
        return { cls: 'ord', w: dimension(raw) * s, h: 0, d: 0, spacer: true } as Atom;
      }
      case 'not':
        return null;
      case 'overset':
      case 'underset':
      case 'stackrel': {
        const top = this.arg(Math.min(2, style + 1) as Style);
        const base = this.arg(style);
        const over = name !== 'underset';
        return {
          cls: name === 'stackrel' ? 'rel' : 'ord',
          w: Math.max(top.w, base.w),
          h: over ? base.h + top.h + top.d + 0.1 * s : base.h,
          d: over ? base.d : base.d + top.h + top.d + 0.1 * s,
        };
      }
      case 'underbrace':
      case 'overbrace': {
        const a = this.arg(style);
        return {
          cls: 'ord',
          w: a.w,
          h: name === 'overbrace' ? a.h + 0.5 * s : a.h,
          d: name === 'underbrace' ? a.d + 0.5 * s : a.d,
        };
      }
      case 'sum':
      case 'prod':
      case 'coprod':
      case 'bigcup':
      case 'bigcap':
      case 'bigoplus':
        return { cls: 'op', w: (name === 'prod' ? 0.944 : 1.056) * s, h: 0.75 * s, d: 0.25 * s };
      case 'int':
      case 'oint':
      case 'iint':
        return { cls: 'op', w: (name === 'iint' ? 0.9 : 0.556) * s, h: 0.81 * s, d: 0.31 * s };
      case 'begin':
        return this.environment(style);
      case 'end':
        this.rawArg();
        return null;
      case '\\':
      case 'newline':
      case 'cr':
        return null;
      case 'bmod':
      case 'mod':
        return { cls: 'bin', ...romanWord('mod', s) };
      case 'pmod': {
        const a = this.arg(style);
        return {
          cls: 'ord',
          w: a.w + romanWord('mod', s).w + 1.6 * s,
          h: Math.max(a.h, 0.75 * s),
          d: Math.max(a.d, 0.25 * s),
        };
      }
      default:
        // commande inconnue : une lettre moyenne (les groupes qui suivent se mesurent seuls)
        return { cls: 'ord', w: 0.6 * s, h: 0.7 * s, d: 0 };
    }
  }

  private rawDimension(): string {
    let out = '';
    while (this.peek()?.t === 'char') {
      const v = (this.peek() as { v: string }).v;
      if (!/[0-9.\-a-z]/.test(v)) break;
      out += v;
      this.i++;
      if (/(em|ex|pt|mu|px|mm|cm|in)$/.test(out)) break;
    }
    return out;
  }

  /** `\begin{env} … \end{env}` : tableau de cellules (matrices, systèmes, alignements) */
  private environment(style: Style): Atom {
    const s = STYLE_SCALE[style];
    const env = this.rawArg().trim();
    if (env === 'array' || env === 'tabular') this.rawArg(); // colonnes
    const rows: TexDims[][] = [[]];
    for (;;) {
      const cellAtoms = this.list(
        style,
        (t) => t.t === '&' || (t.t === 'cmd' && (t.v === '\\' || t.v === 'end' || t.v === 'cr')),
      );
      rows[rows.length - 1]!.push(hlist(cellAtoms, style));
      const t = this.peek();
      if (!t) break;
      this.i++;
      if (t.t === '&') continue;
      if (t.t === 'cmd' && t.v === 'end') {
        this.rawArg();
        break;
      }
      rows.push([]);
    }
    if (rows.length > 1 && rows[rows.length - 1]!.every((c) => c.w === 0)) rows.pop();
    const cols = Math.max(...rows.map((r) => r.length));
    const colW: number[] = [];
    for (let c = 0; c < cols; c++) colW.push(Math.max(0, ...rows.map((r) => r[c]?.w ?? 0)));
    const aligned = env.startsWith('align') || env.startsWith('split') || env === 'gathered';
    const gap = aligned ? 0.278 * s : env === 'cases' ? 1 * s : 1 * s;
    const w =
      colW.reduce((a, b) => a + b, 0) + gap * Math.max(0, cols - 1) + (aligned ? 0 : 0.2 * s);
    const rowH = 1.2 * s;
    const total = rows.length * rowH;
    const h = total / 2 + AXIS * s;
    const d = total / 2 - AXIS * s;
    const pair: Record<string, string> = {
      pmatrix: '(',
      bmatrix: '[',
      Bmatrix: '{',
      vmatrix: '|',
      Vmatrix: '|',
    };
    let extra = 0;
    if (pair[env]) extra = 2 * delimWidth(pair[env]!, h, d, s);
    if (env === 'cases' || env === 'dcases') extra = delimWidth('{', h, d, s) + 0.17 * s;
    return { cls: 'inner', w: w + extra, h, d };
  }
}

function dimension(raw: string): number {
  const m = /^(-?[\d.]+)\s*(em|ex|pt|mu|px|mm|cm|in)?/.exec(raw.trim());
  if (!m) return 0;
  const v = Number(m[1]);
  if (!Number.isFinite(v)) return 0;
  switch (m[2]) {
    case 'ex':
      return v * 0.431;
    case 'pt':
      return v / 10;
    case 'mu':
      return v / 18;
    case 'px':
      return v / 16;
    case 'mm':
      return v * 0.2845;
    case 'cm':
      return v * 2.845;
    case 'in':
      return v * 7.227;
    default:
      return v;
  }
}

/** mot en romain (fonctions, `\text{…}`) */
function romanWord(word: string, s: number, keepSpaces = false): TexDims {
  let w = 0;
  let h = 0;
  let d = 0;
  for (const ch of word) {
    if (!keepSpaces && /\s/.test(ch)) continue;
    const g = glyph(/\s/.test(ch) ? ' ' : ch, false, s);
    w += g.w;
    h = Math.max(h, g.h);
    d = Math.max(d, g.d);
  }
  return { w, h, d };
}

function fraction(num: TexDims, den: TexDims, display: boolean, s: number): TexDims {
  let numShift = (display ? 0.677 : 0.394) * s;
  let denShift = (display ? 0.686 : 0.345) * s;
  const clearance = (display ? 3 : 1) * RULE * s;
  const axis = AXIS * s;
  const rule = RULE * s;
  const numGap = numShift - num.d - (axis + rule / 2);
  if (numGap < clearance) numShift += clearance - numGap;
  const denGap = axis - rule / 2 - (den.h - denShift);
  if (denGap < clearance) denShift += clearance - denGap;
  return {
    w: Math.max(num.w, den.w) + 2 * NULL_DELIM * s,
    h: numShift + num.h,
    d: denShift + den.d,
  };
}

function delimWidth(ch: string, h: number, d: number, s: number): number {
  if (ch === '.') return 0;
  const key = DELIM_MIRROR[ch] ?? ch;
  const axis = AXIS * s;
  const half = Math.max(h - axis, d + axis);
  const needed = Math.max(2 * half * 0.901, 2 * half - 0.5 * s);
  if (needed <= 1.0 * s) return (DELIM_NORMAL[key] ?? DELIM_NORMAL[ch] ?? 0.389) * s;
  for (const size of DELIM_SIZES) if (size.total * s >= needed) return (size.w[key] ?? 0.45) * s;
  return (DELIM_SIZES[3]!.w[key] ?? 0.5) * s;
}

/** boîte horizontale : atomes et espaces de TeX entre eux */
function hlist(atoms: Atom[], style: Style): TexDims {
  // un « bin » sans opérande à gauche (début, après rel, open, bin, punct, op) devient « ord »
  const real = atoms.filter((a) => !(a as Atom & { spacer?: boolean }).spacer);
  let prev: Atom | undefined;
  for (const a of real) {
    if (a.cls === 'bin' && (!prev || ['bin', 'op', 'rel', 'open', 'punct'].includes(prev.cls)))
      a.cls = 'ord';
    if (prev?.cls === 'bin' && ['rel', 'close', 'punct'].includes(a.cls)) prev.cls = 'ord';
    prev = a;
  }
  if (prev?.cls === 'bin') prev.cls = 'ord';

  const mu = STYLE_SCALE[style] / 18;
  let w = 0;
  let h = 0;
  let d = 0;
  let last: Atom | undefined;
  for (const a of atoms) {
    const spacer = (a as Atom & { spacer?: boolean }).spacer;
    if (!spacer && last) {
      const rule = SPACING[last.cls][a.cls];
      if (rule && (!rule[1] || style === 0)) w += rule[0] * mu;
    }
    w += a.w;
    h = Math.max(h, a.h);
    d = Math.max(d, a.d);
    if (!spacer) last = a;
  }
  return { w, h, d };
}

/** Dimensions TeX d'une formule (em de sa taille), sans le facteur KaTeX. */
export function texBox(tex: string): TexDims {
  try {
    const p = new TexParser(tokenize(tex));
    return hlist(p.list(0), 0);
  } catch {
    return { w: [...tex].length * 0.5, h: 0.7, d: 0.2 };
  }
}
